import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  InputAdornment,
  MenuItem,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Link,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { cookie } from "../../cookies/cookies";
import Loader from "../../components/common/loader/Loader";
import { formatToLongDate, formatName } from "../../utils/common";

// Actions
import { PRODUCT_LIST } from "../../actions/admin/customer-actions";
import { PERSONAL_SHOPPER_ITEMS_LIST } from "../../actions/PersonalShopperActions";
import { VIRTUAL_LOCKER_GET_ITEM } from "../../actions/VirtualLockerAction";

// Styles
import {
  StyledWrapper,
  ListContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StatusBadge,
  BadgeDot,
  PageHeader,
  PageTitle,
} from "./style";
import { StyledBoxStyle, StyledCustomerSearch, StyledStatusFilter } from "../admin/products/style";

const STATUS_CONFIG = {
  pending:              { bg: "#FFF3CD", color: "#856404", dot: "#F0A500" },
  review:               { bg: "#E8F4FF", color: "#0B65B5", dot: "#0B65B5" },
  wait_to_deliver:      { bg: "#FFF0E0", color: "#CC5500", dot: "#FF8500" },
  un_delivered:         { bg: "#FFE8E0", color: "#CC2200", dot: "#FF4500" },
  recieved:             { bg: "#E0F0FF", color: "#0B4A8C", dot: "#126ABC" },
  delivered:            { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  approved:             { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  rejected:             { bg: "#FFE8EA", color: "#9B0012", dot: "#FF3850" },
  awaiting_approval:    { bg: "#FFF0E0", color: "#7A3D00", dot: "#E17B0B" },
  item_delivered:       { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  shopping_in_progress: { bg: "#E6F3FF", color: "#0B65B5", dot: "#0B65B5" },
  shopping_completed:   { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
};

const FILTER_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "review", label: "Review" },
  { value: "wait_to_deliver", label: "Wait To Deliver" },
  { value: "un_delivered", label: "Un Delivered" },
  { value: "item_delivered", label: "Item Delivered" },
];

export default function ItemList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profileDetails = useSelector((state) => state?.auth?.profileDetails);
  const userType = profileDetails?.user_type || cookie.get("user_type");
  const isAdmin = userType === "admin";
  const isShopper = userType === "personal_shopper";

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Determine path routing prefix
  const getRoutePrefix = () => {
    if (isAdmin) return "/admin/items";
    if (isShopper) return "/personal-shopper/items";
    return "/dashboard/items";
  };

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchItem);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchItem]);

  // Fetch list
  const fetchList = useCallback(() => {
    if (isAdmin) {
      dispatch({
        type: PRODUCT_LIST,
        payload: {
          search: debouncedSearch,
          status: statusFilter,
          page,
          limit: rowsPerPage,
        },
      });
    } else if (isShopper) {
      dispatch({
        type: PERSONAL_SHOPPER_ITEMS_LIST,
        payload: {
          search: debouncedSearch,
          status: statusFilter,
          page,
          limit: rowsPerPage,
        },
      });
    } else {
      // Customer locker items (filtered locally or via product name query)
      dispatch({
        type: VIRTUAL_LOCKER_GET_ITEM,
        payload: {
          product_name: debouncedSearch || undefined,
        },
      });
    }
  }, [dispatch, isAdmin, isShopper, debouncedSearch, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Selectors
  const adminData = useSelector((state) => state?.product);
  const shopperData = useSelector((state) => state?.persionalShopData);
  const customerData = useSelector((state) => state?.virtual);

  // Extract variables
  const isLoading = isAdmin
    ? adminData?.isLoading
    : isShopper
    ? shopperData?.isPersonalShopperItemsListLoading
    : customerData?.isLoading;

  let items = [];
  let totalCount = 0;

  if (isAdmin) {
    items = adminData?.productsList || [];
    totalCount = adminData?.total || 0;
  } else if (isShopper) {
    items = shopperData?.personalShopperItemsList?.data || [];
    totalCount = shopperData?.personalShopperItemsList?.total || 0;
  } else {
    // Customer
    const rawItems = customerData?.lockerGetList?.data || [];
    // Local filter if status is not 'all' since virtualGet doesn't support status query on frontend directly
    const filteredItems = statusFilter === "all"
      ? rawItems
      : rawItems.filter(item => String(item.status).toLowerCase() === statusFilter.toLowerCase());
    
    totalCount = filteredItems.length;
    // Client-side pagination for customer list
    items = filteredItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleView = (itemId) => {
    navigate(`${getRoutePrefix()}/${itemId}`);
  };

  const getStatusStyle = (status) => {
    const raw = String(status || "").toLowerCase();
    return STATUS_CONFIG[raw] || { bg: "#ECEFF1", color: "#607D8B", dot: "#90A4AE" };
  };

  return (
    <StyledWrapper>
      <PageHeader>
        <PageTitle>Items Directory</PageTitle>
      </PageHeader>

      <ListContainer>
        <StyledBoxStyle sx={{ paddingTop: 0, marginBottom: "20px" }}>
          <StyledCustomerSearch
            placeholder="Search by Product Name..."
            variant="outlined"
            value={searchItem}
            onChange={(e) => setSearchItem(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <StyledStatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
          >
            {FILTER_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledStatusFilter>
        </StyledBoxStyle>

        {isLoading ? (
          <Loader minHeight={300} size={50} />
        ) : (
          <TableContainer>
            <StyledTable>
              <StyledTableHead>
                <TableRow>
                  <TableCell>ITEM ID</TableCell>
                  <TableCell>PRODUCT NAME</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>QTY</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell>CREATED ON</TableCell>
                  <TableCell align="center">ACTION</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {items.length > 0 ? (
                  items.map((row, idx) => {
                    const statusStyle = getStatusStyle(row.status);
                    return (
                      <StyledTableRow key={row.id || idx}>
                        <TableCell sx={{ fontWeight: "bold", color: "#0B2373" }}>
                          #{row.id}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.product_name}</TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {formatName(row.order_type || "N/A")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>x {row.quantity}</TableCell>
                        <TableCell>
                          <StatusBadge statusBg={statusStyle.bg} statusColor={statusStyle.color}>
                            <BadgeDot color={statusStyle.dot} />
                            {formatName(row.status || "pending")}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>{formatToLongDate(row.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Link
                            component="button"
                            variant="button"
                            underline="hover"
                            fontWeight="bold"
                            onClick={() => handleView(row.id)}
                            sx={{ color: "primary.main", textTransform: "uppercase" }}
                          >
                            View
                          </Link>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>
                        No items found matching the filter criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StyledTable>
            <TablePagination
              component="div"
              count={totalCount}
              page={page - 1}
              onPageChange={(event, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        )}
      </ListContainer>
    </StyledWrapper>
  );
}
